// External dependencies
import Seo from "@common/seo"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
// Internal dependencies
import { getLayout } from "components/layouts/emptyResearchLayout"
import { useDashboardContext } from "context/dashboardContext"
import { useAccountPreferences } from "@common/context/accountPreferencesContext"
import { getRedirectPath, getHomePageUrl } from "lib/navigation"
import { getCustomerId, removeQueryStringFromPath } from "lib/filters"
import { ToastFromURL } from "@common/toast/toastFromURL"

// Components

import { ContainerLanding } from "@common/container"
import { SITE, THEME } from "configs/theme"
import WorkFlow, {
  WorkFlowPanel,
  WorkFlowSteps,
  WorkFlowTab,
} from "@common/workflow/workflow"
import Logo from "@common/logo"
import Loader from "@common/loader"
import LogoutButton from "components/logoutButton"
import { Header } from "@common/layouts/stickyHeaderFooter"
import CircleTab from "@common/tabs/tab/circleTab"
import SuccessMessage from "@common/successMessage"
import PersonalProfile from "@common/onboarding/personalProfile"
import BusinessProfile from "@common/onboarding/businessProfile"
import OnboardingPlans from "@common/onboarding/onboardingPlans"
import { buttons } from "pages/pricing"
import { updateAccountDetails } from "@common/network"

Onboarding.getLayout = getLayout
export default function Onboarding() {
  const router = useRouter()
  const { step, payment_status } = router.query
  const { permissions } = useDashboardContext()
  const { isDarkMode } = useAccountPreferences()

  const homePath = removeQueryStringFromPath(
    getRedirectPath(router.asPath, "home"),
    "step",
    "payment_status"
  )
  const customerId = getCustomerId()
  const homePageUrl = getHomePageUrl(customerId)

  const [steps] = useState([
    {
      name: "Profile",
      status: "current",
      actionText: "Next",
    },
    {
      name: "Profession",
      status: "next",
      actionText: "next",
    },
    {
      name: "Plans",
      status: "next",
      actionText: "next",
    },
    {
      name: "Done",
      status: "next",
      actionText: "next",
    },
  ])
  const [currentStepIndex, setCurrentStepIndex] = useState(0)

  useEffect(() => {
    if (step) setCurrentStepIndex(Number(step))
  }, [step])

  useEffect(() => {
    if (payment_status == "success") {
      updateAccountDetails({ home_path: homePath })
    }
  }, [payment_status])

  if (!permissions)
    return (
      <div className="h-screen flex justify-center items-center">
        <Loader />
      </div>
    )

  return (
    <>
      <Seo title={`Personal Profile | ${THEME?.seo?.siteName}`} />
      <div className="dark:bg-black ">
        <ToastFromURL
          queryStr="payment_status"
          successMessage={"Payment is Successful"}
          failureMessage="Payment was unsuccessful! Please try again"
        />
        <ContainerLanding>
          <Header className="flex justify-between items-center w-full py-6">
            <Logo
              logoObj={THEME["logo"]}
              version="full"
              isDarkMode={isDarkMode}
              className="h-8 w-auto m-2"
            />
            <LogoutButton />
          </Header>
          <div className="mx-auto">
            <WorkFlow steps={steps} startStepIdx={currentStepIndex}>
              {currentStepIndex !== steps.length - 1 && (
                <div className="w-1/2 md:w-1/3 lg:w-1/4 mx-auto">
                  <div className="flex">
                    <WorkFlowSteps steps={steps}>
                      {({ step, index }) => (
                        <WorkFlowTab key={index} step={step}>
                          {({ isSelected, handleSelect, isCompleted, isLast }) => (
                            <CircleTab
                              isSelected={isSelected}
                              onSelect={handleSelect}
                              isCompleted={isCompleted}
                              isLast={isLast}
                              index={index}
                            />
                          )}
                        </WorkFlowTab>
                      )}
                    </WorkFlowSteps>
                  </div>
                </div>
              )}

              <WorkFlowPanel stepIdx={0} animate>
                {(isCurrentIndex) => {
                  return isCurrentIndex && <PersonalProfile />
                }}
              </WorkFlowPanel>
              <WorkFlowPanel stepIdx={1} animate>
                {(isCurrentIndex) => {
                  return isCurrentIndex && <BusinessProfile />
                }}
              </WorkFlowPanel>
              <WorkFlowPanel stepIdx={2} animate>
                {(isCurrentIndex) => {
                  return (
                    isCurrentIndex && (
                      <OnboardingPlans buttons={buttons} project={SITE} />
                    )
                  )
                }}
              </WorkFlowPanel>
              <WorkFlowPanel stepIdx={3} animate>
                {(isCurrentIndex) => {
                  return (
                    isCurrentIndex && (
                      <div className="h-screen-3/4 flex items-center justify-center flex-col ">
                        <SuccessMessage
                          title="Awesome! You’re all setup 🚀"
                          paragraphs={[
                            "Get started by adding products to analyze and don’t forget to explore various reporting templates.",
                          ]}
                          buttons={[
                            {
                              link: homePageUrl,
                              className: "btn btn-blue",
                              text: "Let's Go",
                            },
                          ]}
                        />
                      </div>
                    )
                  )
                }}
              </WorkFlowPanel>
            </WorkFlow>
          </div>
        </ContainerLanding>
      </div>
    </>
  )
}
